import React, {useCallback, useState} from 'react';
import Joyride, {STATUS} from 'react-joyride';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { finishTour } from '../../library/api/onboarding';
import { setHasTours } from '../../features/account/accountSlice';
import { setTourActive } from '../../features/tour/tourSlice';


// Tooltip that can optionally render progress UI
const BrandTooltip = ({
                          index,
                          size,
                          step,
                          backProps,
                          closeProps,
                          primaryProps,
                          tooltipProps,
                          continuous,
                          t,
                          showProgress = true,
                          onDontShowAgain,
                          allowPrev,
                      }) => {
    const isLast = index === size - 1;

    // Fill to 100% ONLY on last step. Otherwise cap at 99% so it doesn’t look “done.”
    const progressPercent = isLast
        ? 100
        : Math.min(99, Math.floor(((index + 1) / size) * 100));

    return (
        <div
            {...tooltipProps}
            style={{
                padding: 20,
                background: '#fff',
                borderRadius: 10,
                maxWidth: 520,
                boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                fontSize: 16,
                lineHeight: 1.5,
            }}
        >
            {showProgress && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <div style={{ fontSize: 12, color: '#666' }}>{`${index + 1} / ${size}`}</div>
                    <div
                        style={{ flex: '0 0 60%', height: 6, background: '#eee', borderRadius: 999 }}
                        aria-label={t('emission.tour.progress')}
                        role="progressbar"
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-valuenow={progressPercent}
                    >
                        <div
                            style={{
                                width: `${progressPercent}%`,
                                height: '100%',
                                background: '#0A564B',
                                borderRadius: 999,
                                transition: 'width 200ms ease',
                            }}
                        />
                    </div>
                </div>
            )}

            {step.title && (
                <h5 style={{ textAlign: 'center', marginBottom: 12 }}>
                    {step.title}
                </h5>
            )}
            <div style={{ marginBottom: 12 }}>{step.content}</div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                <button
                    onClick={() => onDontShowAgain?.({ status: STATUS.SKIPPED, type: "dont_show_again" })}
                    style={{
                        border: 'none',
                        background: 'transparent',
                        color: '#E17E7F',
                        cursor: 'pointer',
                        fontSize: 12,
                        textDecoration: 'underline',
                    }}
                >
                    {t('dashboard.tour.dontShowAgain')}
                </button>

                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    {allowPrev && index > 0 && backProps && (
                        <button
                            {...backProps}
                            style={{ border: 'none', background: 'transparent', color: '#E17E7F', cursor: 'pointer' }}
                        >
                            {t('emission.tour.back')}
                        </button>
                    )}

                    {isLast ? (
                        <button
                            {...closeProps}
                            style={{
                                backgroundColor: '#0A564B',
                                color: '#fff',
                                border: 'none',
                                borderRadius: 8,
                                padding: '8px 14px',
                            }}
                        >
                            {t('emission.tour.last')}
                        </button>
                    ) : (
                        <button
                            {...primaryProps}
                            style={{
                                backgroundColor: '#0A564B',
                                color: '#fff',
                                border: 'none',
                                borderRadius: 8,
                                padding: '8px 14px',
                            }}
                        >
                            {t('emission.tour.next')}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};


export default function Tour({ menu, onEnd, callback, steps, allowPrev = true, ...joyrideProps }) {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const onDontShowAgain = React.useCallback(async (endData) => {
        try {
            if (menu) {
                await finishTour(menu);
                dispatch(setHasTours(menu));
            } else {
                console.warn('[Tour] Missing tour key; skipping finishTour()');
            }
        } finally {
            dispatch(setTourActive(false));
            onEnd?.(endData);
        }
    }, [menu, onEnd, dispatch]);

    const handleJoyrideCallback = async (data) => {
        const { index, status, type } = data;

        // active flag
        if (type === 'tour:start' || status === STATUS.RUNNING) {
            dispatch(setTourActive(true));
        }

        // run customAction
        const currentStep = steps?.[index];
        if (currentStep?.customAction) {
            await currentStep.customAction(data);
        }

        // Forward to external callback if provided
        if (typeof callback === 'function') {
            callback(data);
        }

        // END conditions (this is the important part)
        if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
            dispatch(setTourActive(false));

            // Mark tour as completed in backend ONLY when finished
            if (status === STATUS.FINISHED && menu) {
                try {
                    await finishTour(menu);
                    dispatch(setHasTours(menu));
                } catch (e) {
                    console.warn('[Tour] finishTour failed', e);
                }
            }

            // IMPORTANT: pass Joyride data to parent
            onEnd?.(data);
        }
    };

    // Bind our tooltip to the caller's showProgress prop
    const BoundTooltip = (props) => (
        <BrandTooltip
            {...props}
            t={t}
            showProgress={joyrideProps.showProgress !== false}
            onDontShowAgain={onDontShowAgain}
            allowPrev={allowPrev}
        />
    );

    return (
        <Joyride
            {...joyrideProps}
            continuous
            steps={steps}
            callback={handleJoyrideCallback}
            tooltipComponent={BoundTooltip}     // always use our tooltip
            locale={{
                back: t('emission.tour.back'),
                close: t('emission.tour.close'),
                last: t('emission.tour.last'),
                next: t('emission.tour.next'),
                skip: t('emission.tour.skip'),
            }}
            styles={{
                options: {
                    primaryColor: '#0A564B',
                    textColor: '#333',
                    zIndex: 10000,
                    ...(joyrideProps.styles?.options || {}),
                },
                tooltip: {
                    zIndex: 10000,
                    ...(joyrideProps.styles?.tooltip || {}),
                },
                ...joyrideProps.styles,
            }}
            disableOverlayClose={joyrideProps.disableOverlayClose ?? true}
            disableCloseOnEsc={joyrideProps.disableCloseOnEsc ?? true}
            spotlightClicks={joyrideProps.spotlightClicks ?? false}
        />
    );
}
